import { useEffect, useMemo, useState } from "react";
import { api, mediaUrl, asArray, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTick } from "@/context/LiveContext";
import { toast } from "@/lib/toast";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import VenueForm from "@/components/VenueForm";
import { SmartImg } from "@/components/VenueCard";
import DashboardShell, { Tile, Panel } from "@/components/DashboardShell";
import PillSelect from "@/components/PillSelect";
import { CATEGORIES, catLabel, isService, isFood } from "@/lib/categories";
import { paymentsStatus, payForPlan } from "@/lib/razorpay";
import { emailError, phoneError, cleanPhoneInput } from "@/lib/validate";
import { withDefaults } from "@/lib/pricing";
import { useBackendHealth } from "@/components/admin/SystemStatus";
import { Plus, Home as HomeIcon, Calendar, Package, Users, CreditCard, Trash2, Pencil, MessageCircle, Phone, Eye, Inbox, BadgeCheck, Sparkles, CalendarClock, UserPlus, Copy, ExternalLink, LayoutGrid, ShieldCheck, Loader2 } from "lucide-react";

const WA_TEMPLATES = (i, biz, owner) => [
  { k: "thanks", l: "Thank & confirm availability", t: `Namaste ${i.name}, thank you for your inquiry about ${biz} on DartMyVenue. Your date ${i.event_date || "(to be confirmed)"} for ${i.pax || "your"} guests is available. May I share packages & a quick walkthrough? — ${owner}` },
  { k: "visit", l: "Invite for a site visit", t: `Namaste ${i.name}, this is ${owner} from ${biz}. We'd love to host you for a site visit this week — which day suits you? We'll keep a tasting/decor sample ready. — DartMyVenue` },
  { k: "quote", l: "Send a quote", t: `Namaste ${i.name}, here's an indicative quote from ${biz} for ${i.pax || "your"} guests on ${i.event_date || "your date"}: (add amount). It includes venue, basic décor & service. Happy to customise — ${owner}` },
  { k: "follow", l: "Gentle follow-up", t: `Namaste ${i.name}, just checking in from ${biz} — are you still considering us for ${i.event_date || "your event"}? Dates are filling fast for the season; happy to hold yours for 48 hours. — ${owner}` },
];
const waLink = (phone, text) => { const d = (phone || "").replace(/\D/g, ""); return `https://wa.me/${d.length === 10 ? "91" + d : d}?text=${encodeURIComponent(text)}`; };

export default function OwnerDashboard() {
  const { user } = useAuth();
  const tick = useTick();
  const [sp, setSp] = useSearchParams();
  const [tab, setTabState] = useState(sp.get("tab") || "overview");
  const [venues, setVenues] = useState([]);
  const [subs, setSubs] = useState([]);
  const [pricing, setPricing] = useState({});
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState({});
  const [form, setForm] = useState(null); // null | "new" | venue
  const [selectedVenue, setSelectedVenue] = useState(null);
  const { outdated } = useBackendHealth();

  const setTab = (k) => { setTabState(k); const n = new URLSearchParams(sp); n.set("tab", k); setSp(n, { replace: true }); };

  const load = () => {
    api.get("/my/venues").then(r=>setVenues(asArray(r.data))).catch(()=>{});
    api.get("/subscriptions").then(r=>setSubs(asArray(r.data))).catch(()=>{});
    api.get("/config/pricing").then(r=>setPricing(withDefaults(r.data))).catch(()=>setPricing(withDefaults({})));
    api.get("/inquiries").then(r=>setInquiries(asArray(r.data))).catch(()=>{});
    api.get("/my/stats").then(r=>setStats(r.data || {})).catch(()=>{});
  };
  useEffect(()=>{ if (!form) load(); }, [tick]); // eslint-disable-line react-hooks/exhaustive-deps

  const bizType = useMemo(() => {
    const cats = new Set(venues.map(v=>v.category));
    if (cats.size === 1) { const k = [...cats][0]; if (isFood(k)) return { label: "Caterer", noun: "catering service", icon: Package }; if (isService(k)) return { label: catLabel(k), noun: "listing", icon: Sparkles }; }
    return { label: "Business", noun: "listing", icon: HomeIcon };
  }, [venues]);

  const del = async (v) => { if (!window.confirm(`Delete ${v.name}? This cannot be undone.`)) return; await api.delete(`/venues/${v.id}`); toast.success("Listing removed"); load(); };
  const daysLeft = stats.next_expiry ? Math.ceil((new Date(stats.next_expiry) - Date.now()) / 86400000) : null;

  const items = [
    { key: "overview", label: "Overview", Icon: LayoutGrid },
    { key: "venues", label: "My listings", Icon: bizType.icon, badge: venues.length || null },
    { key: "calendar", label: "Availability", Icon: Calendar },
    { key: "inquiries", label: "Inquiries", Icon: Inbox, badge: inquiries.length || null },
    { key: "staff", label: "Staff", Icon: UserPlus },
    { key: "subscription", label: "Plans & payments", Icon: CreditCard },
  ];

  return (
    <DashboardShell title={`Namaste, ${user?.name || "there"}`} subtitle={`Manage your ${bizType.noun}s, dates, inquiries and plan.`} items={items} active={tab} onSelect={setTab}
      actions={<button onClick={()=>setForm("new")} className="btn-wine" data-testid="add-venue-btn"><Plus className="w-4 h-4"/> Add listing</button>}>
      {outdated && (
        <div className="mb-6 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-[14px] text-amber-900" data-testid="owner-server-outdated">
          <b>DartMyVenue is finishing a server update.</b> Photo uploads, adding staff and online payments start working as soon as it's installed. Your listings and inquiries are safe.
        </div>
      )}
      {tab==="overview" && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-3 2xl:grid-cols-6 gap-3">
            <Tile icon={Eye} label="Page views" value={(stats.views ?? 0).toLocaleString("en-IN")}/>
            <Tile icon={Inbox} label="Inquiries" value={stats.inquiries ?? 0}/>
            <Tile icon={Calendar} label="Booked dates" value={stats.bookings ?? 0}/>
            <Tile icon={BadgeCheck} label="Live listings" value={`${stats.approved ?? 0}/${stats.venues ?? 0}`}/>
            <Tile icon={Sparkles} label="Premium" value={stats.premium ?? 0}/>
            <Tile icon={CalendarClock} label="Plan ends in" value={daysLeft == null ? "–" : daysLeft < 0 ? "Ended" : `${daysLeft} days`} tone={daysLeft != null && daysLeft <= 30 ? "alert" : "default"}/>
          </div>
          <Overview venues={venues} inquiries={inquiries} daysLeft={daysLeft} setTab={setTab}/>
        </div>
      )}
      {tab==="venues" && (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
          {venues.map(v=>(
            <div key={v.id} className="rounded-[20px] bg-white border border-[color:var(--line)] overflow-hidden" data-testid={`owner-venue-${v.id}`}>
              <div className="relative h-44"><SmartImg src={v.images?.[0]} category={v.category} className="h-44 w-full object-cover" alt={v.name}/>
                <span className="absolute top-3 left-3 text-[12px] px-2.5 py-0.5 rounded-full bg-white/95 text-[color:var(--maroon)] font-semibold">{catLabel(v.category)}</span>
                <span className="absolute top-3 right-3 text-[12px] px-2.5 py-0.5 rounded-full bg-black/45 text-white">{(v.images||[]).length} photos · {(v.videos||[]).length} videos</span></div>
              <div className="p-4">
                <div className="font-display text-[22px] leading-tight text-[color:var(--maroon)]">{v.name}</div>
                <div className="text-[13px] text-neutral-500 mt-0.5">{v.city} · {v.views || 0} views</div>
                <div className="mt-2 flex gap-1.5 text-[12px] flex-wrap">
                  <span className={`px-2 py-0.5 rounded-full ${v.is_approved ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{v.is_approved ? "Live" : "Waiting for review"}</span>
                  {v.highlighted && <span className="px-2 py-0.5 rounded-full bg-[color:var(--gold-2)] text-[color:var(--maroon-2)] font-semibold">Premium</span>}
                  {v.subscription_until && <span className="px-2 py-0.5 rounded-full bg-[color:var(--ivory)]">Plan till {new Date(v.subscription_until).toLocaleDateString("en-IN", {day:"numeric", month:"short", year:"numeric"})}</span>}
                </div>
                <div className="mt-4 grid grid-cols-4 gap-1.5">
                  <button onClick={()=>setForm(v)} className="btn-ghost !py-2 !px-2 !text-[13px]" title="Edit details, photos & videos" data-testid={`edit-venue-${v.id}`}><Pencil className="w-3.5 h-3.5"/> Edit</button>
                  <button onClick={()=>{ setSelectedVenue(v); setTab("calendar"); }} className="btn-ghost !py-2 !px-2" title="Availability"><Calendar className="w-3.5 h-3.5"/></button>
                  <Link to={`/property/${v.id}`} className="btn-ghost !py-2 !px-2" title="View public page"><ExternalLink className="w-3.5 h-3.5"/></Link>
                  <button onClick={()=>del(v)} className="!py-2 !px-2 rounded-full border border-rose-200 text-rose-600 hover:bg-rose-600 hover:text-white transition-colors flex items-center justify-center press" title="Delete" data-testid={`delete-venue-${v.id}`}><Trash2 className="w-3.5 h-3.5"/></button>
                </div>
              </div>
            </div>
          ))}
          {!venues.length && <div className="col-span-full text-center py-16 text-neutral-500 border-2 border-dashed border-[color:var(--line)] rounded-3xl">No listings yet. <button onClick={()=>setForm("new")} className="text-[color:var(--maroon)] font-semibold underline">Add your first listing</button> — photos and videos can be uploaded straight from your phone.</div>}
        </div>
      )}
      {tab==="calendar" && <AvailabilityManager venues={venues} selected={selectedVenue} setSelected={setSelectedVenue}/>}
      {tab==="inquiries" && <Inquiries inquiries={inquiries} venues={venues} owner={user?.name}/>}
      {tab==="staff" && <StaffPanel/>}
      {tab==="subscription" && <SubscriptionPanel pricing={pricing} subs={subs} venues={venues} onDone={load}/>}

      {form && <VenueForm initial={form === "new" ? null : form} onClose={()=>setForm(null)} onSaved={()=>{ setForm(null); load(); }}/>}
    </DashboardShell>
  );
}

function Overview({ venues, inquiries, daysLeft, setTab }) {
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <Panel className="lg:col-span-2" title="Latest inquiries" action={<button onClick={()=>setTab("inquiries")} className="text-[14px] text-[color:var(--maroon)] font-semibold hover:underline">View all</button>}>
        <div className="divide-y divide-[color:var(--line)]">
          {inquiries.slice(0,6).map(i=>(
            <div key={i.id} className="py-3 flex items-center justify-between gap-3">
              <div className="min-w-0"><div className="font-semibold text-[15px]">{i.name}</div><div className="text-[13px] text-neutral-500 truncate">{i.event_date || "Date not decided"} · {i.pax || "?"} guests · {venues.find(v=>v.id===i.venue_id)?.name || "—"}</div></div>
              <a href={waLink(i.phone, `Namaste ${i.name}, thanks for your inquiry on DartMyVenue!`)} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-emerald-600 text-white flex items-center justify-center shrink-0 press" title="WhatsApp"><MessageCircle className="w-4 h-4"/></a>
            </div>
          ))}
          {!inquiries.length && <div className="py-10 text-center text-neutral-500 text-[15px]">No inquiries yet — share your listing link to get the first one.</div>}
        </div>
      </Panel>
      <div className="space-y-6">
        <Panel title="Your plan">
          <div className="font-num text-[34px] leading-none text-[color:var(--ink)]">{daysLeft == null ? "Not active" : daysLeft < 0 ? "Ended" : `${daysLeft} days left`}</div>
          <p className="text-[14px] text-neutral-600 mt-2">{daysLeft != null && daysLeft <= 30 ? "Renew now to keep your listing in search." : "We'll remind you by email and in-app before it ends."}</p>
          <button onClick={()=>setTab("subscription")} className="btn-wine w-full mt-4">Renew or upgrade</button>
        </Panel>
        <Panel title="Share your listing">
          {venues.slice(0,3).map(v=>(
            <button key={v.id} onClick={()=>{ navigator.clipboard?.writeText(`${window.location.origin}/property/${v.id}`); toast.success("Link copied"); }} className="mt-2 w-full text-left text-[14px] flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl bg-[color:var(--cream)] hover:bg-[color:var(--ivory)] press"><span className="truncate">{v.name}</span><Copy className="w-4 h-4 shrink-0 text-[color:var(--maroon)]"/></button>
          ))}
          {!venues.length && <div className="text-[14px] text-neutral-500">Add a listing first.</div>}
        </Panel>
      </div>
    </div>
  );
}

function Inquiries({ inquiries, venues, owner }) {
  const [open, setOpen] = useState(null);
  return (
    <div className="space-y-3">
      {inquiries.map(i=>{
        const biz = venues.find(v=>v.id===i.venue_id)?.name || "our venue";
        return (
          <div key={i.id} className="card-luxury p-4 md:p-5" data-testid={`inquiry-${i.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-semibold text-[color:var(--ink)]">{i.name} <span className="text-xs font-normal text-neutral-500">· {new Date(i.created_at).toLocaleDateString("en-IN", {day:"numeric", month:"short"})}</span></div>
                <div className="text-xs text-neutral-600 mt-0.5">{biz} · {i.event_date || "date TBD"} · {i.pax || "?"} guests</div>
                {i.message && <p className="text-sm text-neutral-700 mt-2 leading-relaxed">{i.message}</p>}
              </div>
              <div className="flex gap-2">
                <a href={`tel:${i.phone}`} className="btn-ghost !py-2 !px-3 text-xs"><Phone className="w-3.5 h-3.5"/> {i.phone}</a>
                <button onClick={()=>setOpen(open===i.id ? null : i.id)} className="!py-2 !px-3 text-xs rounded-full bg-emerald-600 text-white font-semibold inline-flex items-center gap-1.5 hover:bg-emerald-700" data-testid={`wa-toggle-${i.id}`}><MessageCircle className="w-3.5 h-3.5"/> WhatsApp</button>
              </div>
            </div>
            <AnimatePresence>
              {open===i.id && (
                <motion.div initial={{height:0, opacity:0}} animate={{height:"auto", opacity:1}} exit={{height:0, opacity:0}} className="overflow-hidden">
                  <div className="mt-4 grid sm:grid-cols-2 gap-2">
                    {WA_TEMPLATES(i, biz, owner || "the team").map(t=>(
                      <a key={t.k} href={waLink(i.phone, t.t)} target="_blank" rel="noreferrer" className="rounded-2xl border border-[color:var(--line)] bg-white p-3 hover:border-emerald-600 transition-colors block" data-testid={`wa-template-${t.k}`}>
                        <div className="text-xs font-semibold text-emerald-700 inline-flex items-center gap-1"><MessageCircle className="w-3 h-3"/> {t.l}</div>
                        <div className="text-[11px] text-neutral-600 mt-1 line-clamp-3">{t.t}</div>
                      </a>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
      {!inquiries.length && <div className="text-center py-16 text-neutral-500 border border-dashed border-[color:var(--gold)]/50 rounded-3xl">No inquiries yet.</div>}
    </div>
  );
}

function StaffPanel() {
  const [staff, setStaff] = useState([]);
  const EMPTY_STAFF = { name:"", email:"", phone:"", password:"", staff_role:"event_manager" };
  const [f, setF] = useState(EMPTY_STAFF);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/users/staff").then(r=>setStaff(asArray(r.data))).catch(()=>{});
  useEffect(()=>{ load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    const bad = !f.name.trim() ? "Enter a name" : emailError(f.email, { required: true }) || phoneError(f.phone) || (!f.password ? "Choose a password" : "");
    if (bad) { toast.error(bad); return; }
    setBusy(true);
    try {
      await api.post("/users/staff", { name: f.name.trim(), email: f.email.trim().toLowerCase(), password: f.password, staff_role: f.staff_role || "event_manager", phone: f.phone || null });
      toast.success(`${f.name.trim()} added. They can sign in with ${f.email.trim().toLowerCase()}`);
      setF(EMPTY_STAFF); load();
    } catch (er) {
      const st = er?.response?.status;
      toast.error(st === 404 || st === 405 ? "Adding staff needs the updated server. Ask the admin to update the backend." : formatApiErrorDetail(er?.response?.data?.detail) || "Could not add staff");
    } finally { setBusy(false); }
  };
  const del = async (id) => { await api.delete(`/users/staff/${id}`); load(); };
  return (
    <div className="grid md:grid-cols-3 gap-6">
      <form noValidate onSubmit={add} className="card-luxury p-6 space-y-3">
        <div className="font-display text-2xl text-[color:var(--maroon)]">Invite staff</div>
        <input required placeholder="Name" value={f.name} onChange={e=>setF({...f, name:e.target.value})} className="input" data-testid="staff-name"/>
        <input required type="email" inputMode="email" placeholder="Email (used to sign in)" value={f.email} onChange={e=>setF({...f, email:e.target.value})} className="input" data-testid="staff-email"/>
        <input type="tel" inputMode="tel" placeholder="Mobile (optional)" value={f.phone} onChange={e=>setF({...f, phone:cleanPhoneInput(e.target.value)})} className="input" data-testid="staff-phone"/>
        <input required type="text" autoComplete="new-password" placeholder="Temporary password" value={f.password} onChange={e=>setF({...f, password:e.target.value})} className="input" data-testid="staff-password"/>
        <select value={f.staff_role} onChange={e=>setF({...f, staff_role:e.target.value})} className="input"><option value="event_manager">Event manager</option><option value="receptionist">Receptionist</option><option value="sales">Sales</option></select>
        <button disabled={busy} className="btn-wine w-full" data-testid="staff-add">{busy ? "Adding…" : "Add staff"}</button>
      </form>
      <div className="md:col-span-2 card-luxury p-4">
        <div className="text-sm font-semibold mb-2">Team ({staff.length})</div>
        <div className="divide-y divide-[color:var(--line)]">
          {staff.map(s=>(<div key={s.id} className="py-2.5 flex items-center justify-between text-sm"><div><div className="font-medium">{s.name}</div><div className="text-xs text-neutral-500">{s.email} · {s.staff_role?.replace("_"," ")}</div></div><button onClick={()=>del(s.id)} className="text-rose-600 p-2"><Trash2 className="w-4 h-4"/></button></div>))}
          {!staff.length && <div className="py-8 text-center text-neutral-500 text-sm">No staff yet.</div>}
        </div>
      </div>
    </div>
  );
}

function AvailabilityManager({ venues, selected, setSelected }) {
  const [bookings, setBookings] = useState([]);
  const [date, setDate] = useState("");
  const [slot, setSlot] = useState("full_day");
  useEffect(()=>{ if (!selected && venues.length) setSelected(venues[0]); }, [venues]);
  useEffect(()=>{ if (selected) api.get(`/venues/${selected.id}/bookings`).then(r=>setBookings(r.data)); }, [selected]);
  const addBooking = async (e) => {
    e.preventDefault();
    try { await api.post(`/venues/${selected.id}/bookings`, { venue_id: selected.id, date, slot }); toast.success("Marked booked"); setDate(""); const r = await api.get(`/venues/${selected.id}/bookings`); setBookings(r.data); }
    catch { toast.error("Failed"); }
  };
  if (!selected) return <div className="text-neutral-500">Add a listing first.</div>;
  return (
    <div className="grid md:grid-cols-3 gap-6">
      <div className="card-luxury p-4">
        <div className="label">Select listing</div>
        <div className="space-y-1.5">{venues.map(v=>(<button key={v.id} onClick={()=>setSelected(v)} className={`w-full text-left px-3 py-2 rounded-xl text-sm ${selected?.id===v.id ? "bg-[color:var(--maroon)] text-[color:var(--cream-primary)]" : "hover:bg-[color:var(--ivory)]"}`}>{v.name}</button>))}</div>
      </div>
      <div className="md:col-span-2 space-y-4">
        <form onSubmit={addBooking} className="card-luxury p-4 flex flex-wrap items-end gap-3">
          <div><div className="label">Date</div><input required type="date" value={date} onChange={e=>setDate(e.target.value)} className="input" data-testid="booking-date"/></div>
          <div><div className="label">Slot</div><select value={slot} onChange={e=>setSlot(e.target.value)} className="input"><option value="full_day">Full day</option><option value="morning">Morning</option><option value="afternoon">Afternoon</option><option value="evening">Evening</option></select></div>
          <button className="btn-wine" data-testid="add-booking-btn">Mark booked</button>
        </form>
        <div className="card-luxury p-4">
          <div className="text-sm font-semibold mb-2">Booked dates ({bookings.length})</div>
          <div className="flex flex-wrap gap-2">
            {bookings.map((b, i)=>(<span key={i} className="text-xs px-3 py-1 rounded-full bg-rose-50 text-rose-700 border border-rose-100">{b.date} · {b.slot.replace("_"," ")}</span>))}
            {!bookings.length && <div className="text-neutral-500 text-sm">No bookings yet.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

function SubscriptionPanel({ pricing, subs, venues, onDone }) {
  const myCats = Array.from(new Set(venues.map((v) => v.category)));
  const [plan, setPlan] = useState(myCats[0] || "banquet_hall");
  const [months, setMonths] = useState(12);
  const [venueId, setVenueId] = useState("");
  const [enabled, setEnabled] = useState(null);
  const [payReason, setPayReason] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { paymentsStatus().then((st) => { setEnabled(st.enabled); setPayReason(st.reason); }); }, []);
  useEffect(() => { if (!venueId && venues[0]) setVenueId(venues[0].id); }, [venues, venueId]);

  const term = months === 12 ? "12m" : "6m";
  const amount = plan === "premium_slot" ? Number(pricing?.premium_slot?.[term] || 0) : Number(pricing?.categories?.[plan]?.[term] || 0);
  const options = [
    ...CATEGORIES.filter((c) => pricing?.categories?.[c.key]).map((c) => ({ value: c.key, label: `${c.plural} plan`, hint: myCats.includes(c.key) ? "You list in this category" : c.blurb, Icon: c.Icon })),
    ...(pricing?.categories?.combo_all ? [{ value: "combo_all", label: "All-in-one plan", hint: "Every category on one plan", Icon: LayoutGrid }] : []),
    ...(pricing?.premium_slot ? [{ value: "premium_slot", label: "Premium spotlight", hint: "Top-10 slot for one listing in its city", Icon: Sparkles }] : []),
  ];

  const pay = async () => {
    setBusy(true);
    try {
      const r = await payForPlan({ plan, months, venue_id: plan === "premium_slot" ? venueId : undefined });
      toast.success(`Payment received — active till ${new Date(r.ends_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}`);
      onDone();
    } catch (e) {
      if (!e?.cancelled) toast.error(e?.response?.data?.detail ? formatApiErrorDetail(e.response.data.detail) : (e.message || "Payment failed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6">
      <Panel title="Renew or buy a plan">
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block"><span className="label">Plan</span><PillSelect value={plan} onChange={setPlan} options={options} testId="sub-plan" minWidth={300}/></label>
          <label className="block"><span className="label">Length</span>
            <div className="grid grid-cols-2 gap-1 p-1 rounded-[14px] bg-[color:var(--cream)] border border-[color:var(--line)]">
              {[6, 12].map((m) => <button key={m} type="button" onClick={() => setMonths(m)} className={`py-2.5 rounded-[10px] text-[15px] font-semibold ${months === m ? "bg-[color:var(--maroon)] text-white" : "text-[color:var(--maroon)]"}`}>{m} months</button>)}
            </div>
          </label>
          {plan === "premium_slot" && (
            <label className="block sm:col-span-2"><span className="label">Listing to feature</span>
              <PillSelect value={venueId} onChange={setVenueId} options={venues.map((v) => ({ value: v.id, label: v.name, hint: `${catLabel(v.category)} · ${v.city}` }))} placeholder="Choose a listing" testId="sub-venue"/>
            </label>
          )}
        </div>
        <div className="mt-6 rounded-[18px] bg-[color:var(--cream)] border border-[color:var(--line)] p-5 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-[14px] text-[color:var(--ink-2)]">You pay</div>
            <div className="font-num text-[44px] leading-none text-[color:var(--maroon)] mt-1">₹{amount.toLocaleString("en-IN")}</div>
            <div className="text-[13px] text-neutral-500 mt-1">for {months} months · added to your current end date</div>
          </div>
          <button onClick={pay} disabled={busy || !enabled || !amount || (plan === "premium_slot" && !venueId)} className="btn-wine !py-3.5 !px-7" data-testid="pay-now-btn">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin"/> Opening Razorpay…</> : <>Pay ₹{amount.toLocaleString("en-IN")}</>}
          </button>
        </div>
        {enabled === false && (
          <div className="mt-4 p-3.5 rounded-xl bg-amber-50 border border-amber-200 text-[14px] text-amber-900" data-testid="pay-off-reason">
            {payReason === "outdated_server" ? "Online payment needs the updated DartMyVenue server, which hasn't been installed yet. Until then, call or WhatsApp us to renew and we'll update your plan."
              : payReason === "unreachable" ? "Couldn't reach the payment service just now. Check your connection and reload this page."
              : "Online payment isn't switched on yet. Call or WhatsApp DartMyVenue to renew — we'll update your plan from our side."}
          </div>
        )}
        <div className="mt-4 text-[13px] text-neutral-500 inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4"/> Payments are processed by Razorpay — UPI, cards, net banking and wallets.</div>
      </Panel>
      <Panel title="Payment history">
        <div className="space-y-1">
          {subs.map(s=>(<div key={s.id} className="flex justify-between items-start gap-3 border-b border-[color:var(--line)] py-2.5"><div className="min-w-0"><div className="text-[15px] font-semibold">{catLabel(s.plan)} · {s.months} months</div><div className="text-[13px] text-neutral-500">₹{Number(s.amount||0).toLocaleString("en-IN")} · {new Date(s.paid_at || s.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}{s.ends_at ? ` · till ${new Date(s.ends_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}` : ""}</div></div><span className={`text-[12px] px-2 py-1 rounded-full shrink-0 ${s.status==="paid" ? "bg-emerald-50 text-emerald-700" : "bg-neutral-100 text-neutral-600"}`}>{s.status === "paid" ? "Paid" : "Not completed"}</span></div>))}
          {!subs.length && <div className="text-neutral-500 text-[15px] py-6 text-center">No payments yet.</div>}
        </div>
      </Panel>
    </div>
  );
}
